import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { House, Package, Clock, PlusCircle, ArrowsClockwise, Gear, SignOut, Sun, Moon, Users as UsersIcon } from "@phosphor-icons/react";

const baseItems = [
  { to: "/", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/delivered", label: "Delivered Orders", icon: Package, testid: "nav-delivered" },
  { to: "/pending", label: "Pending Orders", icon: Clock, testid: "nav-pending" },
  { to: "/add-sale", label: "Add Sale", icon: PlusCircle, testid: "nav-add-sale" },
  { to: "/sync-log", label: "Sync Log", icon: ArrowsClockwise, testid: "nav-sync-log" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

// Shown in the header above the signed-in user's name, in place of "Signed in".
const ROLE_LABELS = { owner: "Owner", admin: "Admin", user: "User", porter: "Porter" };

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const onLogout = () => { logout(); nav("/login", { replace: true }); };

  const items = (user?.role === "owner" || user?.role === "admin")
    ? [...baseItems, { to: "/users", label: "Team & Roles", icon: UsersIcon, testid: "nav-users" }]
    // Sales Reps (role="user") only get Dashboard, Delivered Orders, and
    // Pending Orders — Add Sale, Sync Log, Settings, and Team & Roles are
    // all hidden for them.
    : baseItems.filter((it) => ["/", "/delivered", "/pending"].includes(it.to));

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="hidden lg:flex flex-col w-[260px] border-r border-border bg-card">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
          <div className="w-9 h-9 rounded-md bg-white border border-border flex items-center justify-center p-0.5">
            <img src="/sfc-logo.png" alt="SFC" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-base leading-tight">SFC Go</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sandeep Forms Centre</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-signal/10 text-signal border-l-2 border-signal font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="w-8 h-8 rounded-md bg-white border border-border flex items-center justify-center p-0.5">
              <img src="/sfc-logo.png" alt="SFC" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-sm">SFC Go</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Sandeep Forms Centre</div>
            </div>
          </div>
          <div className="hidden lg:block text-xs uppercase tracking-widest text-muted-foreground">
            Sales Entry & Delivery Management
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="w-9 h-9 rounded-md border border-border flex items-center justify-center hover:bg-muted transition-colors"
              aria-label="toggle theme"
            >
              {theme === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
            </button>
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role] || "Signed in"}</span>
              <span className="text-sm font-medium" data-testid="user-name">{user?.name || user?.email}</span>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-button-mobile"
              aria-label="Sign out"
              title="Sign out"
              className="lg:hidden w-9 h-9 rounded-md border border-border flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground"
            >
              <SignOut size={16} weight="duotone" />
            </button>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="lg:hidden flex overflow-x-auto gap-2 px-3 py-2.5 border-b border-border bg-card sticky top-16 z-20 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                `flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? "bg-signal text-white shadow-sm" : "bg-muted text-muted-foreground active:bg-border"
                }`
              }
            >
              <it.icon size={15} weight="duotone" /> {it.label}
            </NavLink>
          ))}
          <button
            onClick={onLogout}
            data-testid="logout-button-mobile-nav"
            className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-muted text-signal active:bg-signal/10"
          >
            <SignOut size={15} weight="duotone" /> Sign out
          </button>
        </nav>

        <main className="flex-1 p-3.5 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}